import type {
  LlmUsageDimension,
  LlmUsageFilters,
  LlmUsageRow,
  LlmUsageSummary,
} from '../../types/llmUsage'
import { USAGE_WINDOWS } from '../../types/llmUsage'

/**
 * The report's pure shaping logic. Kept free of React — like `llmLanesModel.ts`
 * beside it — so the rules that decide whether money is shown, and what an
 * unqueued call looks like, can be tested without mounting anything.
 */

/**
 * Does this payload carry money?
 *
 * Read off the DATA, never off a prop or a role check in the component. The
 * job-scoped endpoint omits `cost` entirely, so the server stays the single
 * authority on who sees margin: if the field is not there, no cost column
 * renders, and no amount of client-side wishing brings it back.
 *
 * Deliberately `!== undefined` rather than truthiness — a genuinely free call
 * costs 0, and 0 is a real number we should show on an admin mount.
 */
export const payloadHasCost = (
  rows: LlmUsageRow[] | undefined,
  summary: LlmUsageSummary | undefined,
): boolean => {
  if (summary?.total_cost !== undefined) return true
  return (rows ?? []).some(r => r.cost !== undefined)
}

/**
 * Was this call queued at all?
 *
 * Only AutoCoder coding and ThemeAI go through the queue. Everything else —
 * table analysis, exec reports, the agent, generic chat — calls the model
 * synchronously. Those rows are complete and correct; they simply have no lane.
 */
export const wasQueued = (row: LlmUsageRow): boolean => Boolean(row.request_id)

/** What to show in the State column. Never "unknown", never an empty cell. */
export const queueLabel = (row: LlmUsageRow): string => {
  if (!wasQueued(row)) return 'Direct call'
  return row.state ? row.state.charAt(0).toUpperCase() + row.state.slice(1) : 'Queued'
}

/**
 * How long the call took, in ms — start to finish where both are known.
 * Returns null rather than 0 when it cannot be known, so the UI can say nothing
 * instead of claiming it was instant.
 */
export const durationMs = (row: LlmUsageRow): number | null => {
  if (!row.started_at || !row.completed_at) return null
  const start = Date.parse(row.started_at)
  const end = Date.parse(row.completed_at)
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null
  return end - start
}

/** Compact duration: 940ms, 3.2s, 1m 04s. */
export const formatDuration = (ms: number | null): string => {
  if (ms === null) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  const mins = Math.floor(ms / 60_000)
  const secs = Math.round((ms % 60_000) / 1000)
  return `${mins}m ${String(secs).padStart(2, '0')}s`
}

/** Compact thousands, matching the lanes panel: 500000 -> 500k. */
export const formatTokens = (n: number | undefined): string => {
  const v = n ?? 0
  if (v < 1000) return String(v)
  if (v < 1_000_000) return `${(v / 1000).toFixed(v < 10_000 ? 1 : 0)}k`
  return `${(v / 1_000_000).toFixed(1)}M`
}

/**
 * Money, to four decimals. Individual model calls are routinely sub-cent, and
 * rounding them to 2dp would render most of the table as $0.00 — which reads as
 * "free" rather than "small".
 */
export const formatCost = (amount: number | undefined, currency = 'USD'): string => {
  if (amount === undefined) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount)
}

/**
 * Turn a window key into a time range. `now` is injected so this is testable
 * without freezing the clock.
 */
export const windowToRange = (
  key: string,
  now: number = Date.now(),
): { start_time?: number; end_time?: number } => {
  const w = USAGE_WINDOWS.find(x => x.key === key)
  if (!w || w.hours === null) return {}
  return { start_time: now - w.hours * 60 * 60 * 1000 }
}

/**
 * Which filter segments a mount has taken away from the reader.
 *
 * A locked segment is rendered as static text rather than a disabled input: a
 * greyed-out dropdown invites clicking and implies the value could change,
 * which on the job mount it cannot.
 */
export const lockedSegments = (scopeKind: 'admin' | 'job' | 'parent'): string[] => {
  switch (scopeKind) {
    case 'admin':
      return []
    case 'job':
      return ['customer', 'job']
    case 'parent':
      return ['customer', 'job', 'client_ref']
  }
}

/** Strip empty values so the query key stays stable across re-renders. */
export const compactFilters = (filters: LlmUsageFilters): LlmUsageFilters => {
  const out: LlmUsageFilters = {}
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v) && v.length === 0) continue
    ;(out as Record<string, unknown>)[k] = v
  }
  return out
}

/** One grouped breakdown table, described by the dimension it was asked for. */
export interface UsageBreakdown {
  title: string
  testId: string
  rows: LlmUsageDimension[]
  /** Tooltip on the empty-string ("unattributed") row. */
  unattributedHint: string
}

/**
 * Which breakdown the summary carries, if any. The server populates exactly
 * one of `by_user` / `by_label` according to the query's `group_by`; the
 * component renders whichever arrived, with the same columns and the same
 * unattributed bucket, rather than owning one table per dimension.
 */
export const selectBreakdown = (summary: LlmUsageSummary | undefined): UsageBreakdown | null => {
  if (summary?.by_user) {
    return {
      title: 'User',
      testId: 'llm-usage-by-user',
      rows: summary.by_user,
      unattributedHint:
        'Spend billed to no user — background work such as indexing and title generation that runs with no one in scope. It is counted here rather than dropped.',
    }
  }
  if (summary?.by_label) {
    return {
      title: 'Campaign',
      testId: 'llm-usage-by-label',
      rows: summary.by_label,
      unattributedHint:
        'Spend whose batch carried no campaign label — work from before labels existed, or a batch nobody attributed. It is counted here rather than dropped.',
    }
  }
  return null
}
